import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid3x3, List } from 'lucide-react';
import { getProgrammes, getProgrammeById, deleteProgramme, Programme, ProgrammeBase } from '../api/programmes';
import { getProjects } from '../api/projects';
import { Project } from '../types/project';
import ProgrammeForm from '../components/ProgrammeForm';
import ProgrammeTableView from '../components/ProgrammeTableView';
import DeleteProgrammeModal from '../components/DeleteProgrammeModal';
import Button from '../components/Button';
import Card from '../components/Card';
import { CardSkeleton } from '../components/LoadingSkeleton';
import { useAuth } from '../contexts/AuthContext';

const ProgrammesPage: React.FC = () => {
  const navigate = useNavigate();
  const { canManageProgrammes } = useAuth();
  const [programmes, setProgrammes] = useState<Programme[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingProgramme, setEditingProgramme] = useState<Programme | null>(null);
  const [deletingProgramme, setDeletingProgramme] = useState<{ id: string; name: string; projectCount: number } | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [viewMode, setViewMode] = useState<'cards' | 'table'>(() => {
    const saved = localStorage.getItem('programmesView');
    return (saved === 'table' || saved === 'cards') ? saved : 'cards';
  });

  useEffect(() => {
    loadProgrammes();
  }, []);

  // Persist view mode to localStorage
  useEffect(() => {
    localStorage.setItem('programmesView', viewMode);
  }, [viewMode]);

  const loadProgrammes = async () => {
    try {
      const [programmesData, projectsData] = await Promise.all([
        getProgrammes(),
        getProjects()
      ]);
      setProgrammes(programmesData);
      setProjects(projectsData);
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load programmes';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSuccess = (programme: ProgrammeBase) => {
    // A new programme has no projects yet, so its rolled-up metrics are all zero.
    setProgrammes([...programmes, { ...programme, total_projects: 0, total_budget: 0, total_tasks: 0, completed_tasks: 0, completion_percentage: 0 }]);
    setShowForm(false);
  };

  const handleUpdateSuccess = (updatedProgramme: ProgrammeBase) => {
    // Keep the metrics the list already has; the update response carries none.
    setProgrammes(programmes.map(p => p.id === updatedProgramme.id ? { ...p, ...updatedProgramme } : p));
    setEditingProgramme(null);
  };

  const handleEdit = (programme: Programme) => {
    setEditingProgramme(programme);
    setShowForm(false);
  };

  const handleDeleteClick = async (programme: Programme) => {
    try {
      // Fetch programme details to get project count
      const details = await getProgrammeById(programme.id);
      setDeletingProgramme({
        id: programme.id,
        name: programme.name,
        projectCount: details.total_projects || 0,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load programme details';
      setError(errorMessage);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingProgramme) return;

    try {
      await deleteProgramme(deletingProgramme.id);
      setProgrammes(programmes.filter(p => p.id !== deletingProgramme.id));
      setDeletingProgramme(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete programme';
      setError(errorMessage);
    }
  };

  const handleCancelEdit = () => {
    setEditingProgramme(null);
    setShowForm(false);
  };

  const formatDate = (dateString?: string | null): string => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount?: number | null): string => {
    if (!amount) return 'Not set';
    return `R${amount.toLocaleString()}`;
  };

  if (loading) {
    return (
      <div className="max-w-7xl 2xl:max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 py-8">
        <div className="flex justify-between items-center mb-8">
          <div className="h-10 w-32 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
          <div className="h-10 w-40 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          <CardSkeleton showFooter={true} />
          <CardSkeleton showFooter={true} />
          <CardSkeleton showFooter={true} />
          <CardSkeleton showFooter={true} />
          <CardSkeleton showFooter={true} />
          <CardSkeleton showFooter={true} />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl 2xl:max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 py-8">
      <div className="flex flex-wrap gap-3 sm:gap-4 justify-between items-center mb-8">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-primary-600 via-secondary-600 to-accent-600 bg-clip-text text-transparent">Programmes</h1>
        <div className="flex flex-wrap gap-2 sm:gap-3 items-center">
          {/* View Toggle */}
          <div className="flex gap-2 bg-white/70 dark:bg-gray-800/70 backdrop-blur-md border border-white/20 dark:border-gray-700/50 rounded-lg p-1">
            <Button
              variant={viewMode === 'cards' ? 'primary' : 'outline'}
              size="sm"
              leftIcon={Grid3x3}
              onClick={() => setViewMode('cards')}
              title="Card View"
            >
              Cards
            </Button>
            <Button
              variant={viewMode === 'table' ? 'primary' : 'outline'}
              size="sm"
              leftIcon={List}
              onClick={() => setViewMode('table')}
              title="Table View"
            >
              Table
            </Button>
          </div>
          {canManageProgrammes && (
            <Button
              onClick={() => {
                setShowForm(!showForm);
                setEditingProgramme(null);
              }}
              variant="primary"
            >
              {showForm ? 'Cancel' : 'Add New Programme'}
            </Button>
          )}
        </div>
      </div>

      {error && <div className="rounded-lg p-4 bg-error/10 dark:bg-error/20 border border-error/30 text-error">{error}</div>}

      {/* Create Form */}
      {showForm && !editingProgramme && (
        <div className="mb-8">
          <ProgrammeForm onSuccess={handleCreateSuccess} onCancel={() => setShowForm(false)} />
        </div>
      )}

      {/* Edit Form */}
      {editingProgramme && (
        <div className="mb-8">
          <ProgrammeForm
            programme={editingProgramme}
            onSuccess={handleUpdateSuccess}
            onCancel={handleCancelEdit}
          />
        </div>
      )}

      {/* Programmes List */}
      <div>
        <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">
          All Programmes ({programmes.length})
        </h2>

        {programmes.length === 0 ? (
          <Card>
            <Card.Body>
              <p className="text-gray-600 dark:text-gray-400 text-center">
                No programmes found. Create your first programme to get started!
              </p>
            </Card.Body>
          </Card>
        ) : viewMode === 'table' ? (
          <ProgrammeTableView
            programmes={programmes}
            projects={projects}
            onView={(programmeId) => navigate(`/programmes/${programmeId}`)}
            onEdit={handleEdit}
            onDelete={(programmeId) => {
              const programme = programmes.find(p => p.id === programmeId);
              if (programme) handleDeleteClick(programme);
            }}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {programmes.map((programme) => (
              <Card key={programme.id} variant="glass" className="hover:-translate-y-1 transition-all duration-300">
                <Card.Header
                  title={programme.name}
                  subtitle={`Created: ${formatDate(programme.created_at)}`}
                />
                <Card.Body>
                  {programme.description && (
                    <p className="text-sm text-gray-900 dark:text-gray-100 mb-3">{programme.description}</p>
                  )}
                  
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="font-semibold text-gray-900 dark:text-gray-100">Start Date:</span>{' '}
                      <span className="text-gray-900 dark:text-gray-100">{formatDate(programme.start_date)}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-900 dark:text-gray-100">End Date:</span>{' '}
                      <span className="text-gray-900 dark:text-gray-100">{formatDate(programme.end_date)}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="font-semibold text-gray-900 dark:text-gray-100">Budget:</span>{' '}
                      <span className="text-gray-900 dark:text-gray-100 font-bold">{formatCurrency(programme.budget)}</span>
                    </div>
                  </div>
                </Card.Body>
                <Card.Footer>
                  <Button onClick={() => navigate(`/programmes/${programme.id}`)} variant="secondary" size="sm">
                    View Details
                  </Button>
                  {canManageProgrammes && (
                    <>
                      <Button onClick={() => handleEdit(programme)} variant="primary" size="sm">
                        Edit
                      </Button>
                      <Button onClick={() => handleDeleteClick(programme)} variant="danger" size="sm">
                        Delete
                      </Button>
                    </>
                  )}
                </Card.Footer>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deletingProgramme && (
        <DeleteProgrammeModal
          programmeName={deletingProgramme.name}
          projectCount={deletingProgramme.projectCount}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeletingProgramme(null)}
        />
      )}
    </div>
  );
};

export default ProgrammesPage;
